import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { FilterDisplayType } from '@shared/shared/components/filter-bar/models/filter-interfaces';
import { transformObjectToUrlString, transformStringToParamsObject } from '@shared/utils/filter-url-transformer-utils';
import { of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import * as CurrentFiltersAction from '../actions/current-filter.actions';

@Injectable()
export class CurrentFilterEffects {
  constructor(
    private actions$: Actions,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  /**
   * This handles the updating of the url when filters are being applied and removed
   * it will keep existing url query params and only add / remove params relevant to the filtering
   */

  public updateFilterUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CurrentFiltersAction.UPDATE_CURRENT_FILTERS),
      switchMap((action: CurrentFiltersAction.UpdateCurrentFilters) => {
        let filterParamsObject = {};

        if (action.payload) {
          const queryParams: { [x: string]: any } = this.getNewQueryParams(action);
          this.router.navigate([location.pathname], { queryParams });
          filterParamsObject = this.getUpdateFilterParams(queryParams);
        } else {
          const filterParams = this.activatedRoute.snapshot.queryParams['filters'];
          filterParamsObject = transformStringToParamsObject(filterParams);
        }

        return of(new CurrentFiltersAction.UpdateCurrentFiltersSuccess(filterParamsObject));
      })
    )
  );

  public getCurrentFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CurrentFiltersAction.GET_CURRENT_FILTERS),
      switchMap(() => {
        let newQueryParams: any = {};
        let returnFilters: any;
        const splitUrl = this.router.url.replace('#main-content', '').split('?');
        const splitUrlParams = splitUrl[1];

        if (splitUrlParams) {
          newQueryParams = transformStringToParamsObject(splitUrlParams);
        }
        if (newQueryParams.filters) {
          // There are some filters being applied
          const currentFilterList = decodeURIComponent(newQueryParams.filters);
          returnFilters = transformStringToParamsObject(currentFilterList);
        } else {
          returnFilters = {};
        }

        return of(returnFilters).pipe(map((result: any) => new CurrentFiltersAction.GetCurrentFiltersSuccess(result)));
      })
    )
  );

  public clearFilters$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CurrentFiltersAction.CLEAR_CURRENT_FILTERS),
        tap(() => {
          let newQueryParams: any = {};
          const splitUrl = this.router.url.replace('#main-content', '').split('?');
          const splitUrlParams = splitUrl[1];
          if (splitUrlParams) {
            newQueryParams = transformStringToParamsObject(splitUrlParams);
            delete newQueryParams.filters;
            delete newQueryParams.hotelSearchByName;
            if (this.router.url.indexOf('/dining/search') === -1) {
              delete newQueryParams.q;
            }
          }
          localStorage.setItem('entertainment-search', '');
          localStorage.setItem('member-benefits-search', '');
          localStorage.setItem('member-events-search', '');
          localStorage.removeItem('flight-search-filter');
          this.router.navigate([splitUrl[0]], { queryParams: newQueryParams });
        })
      ),
    { dispatch: false }
  );

  public checkFilterValueType(filterValue: any, filterType: FilterDisplayType): string {
    switch (typeof filterValue) {
      case 'string':
        return filterValue;
      case 'object':
        if (filterType === FilterDisplayType.LOCATION) {
          return `${filterValue.lat},${filterValue.long}`;
        } else {
          return JSON.stringify(filterValue);
        }
      default:
        return filterValue;
    }
  }

  public getNewQueryParams(action: CurrentFiltersAction.UpdateCurrentFilters) {
    const newQueryParams = { ...this.activatedRoute.snapshot.queryParams };

    if (!newQueryParams.filters) {
      // If there are no filters being applied currently then create the query param
      newQueryParams.filters = `${action.payload.filterName}=${this.checkFilterValueType(
        action.payload.filterValue,
        action.payload.filterType
      )}`;
      newQueryParams.q = newQueryParams.q || '';
    } else {
      // There are some filters being applied
      const currentFilterList = decodeURIComponent(newQueryParams.filters);
      const tmpFilters = transformStringToParamsObject(currentFilterList);
      // If the filter in the action is not already being applied then add it to the url params or the value exists and needs updating
      if (!tmpFilters[action.payload.filterName] || action.payload.filterValue !== null) {
        tmpFilters[action.payload.filterName] = this.checkFilterValueType(
          action.payload.filterValue,
          action.payload.filterType
        );
      } else {
        // The value is null so the filter needs to be removed
        delete tmpFilters[action.payload.filterName];
      }

      const filterKeys = Object.keys(tmpFilters);
      if (filterKeys.length > 0) {
        for (const param in tmpFilters) {
          if (Array.isArray(tmpFilters[param])) {
            tmpFilters[param] = JSON.stringify(tmpFilters[param]);
          }

          if (tmpFilters[param] === '[]') {
            delete tmpFilters[param];
          }
        }
        newQueryParams.filters = transformObjectToUrlString(tmpFilters);
      } else {
        // remove the filters from the query params as there are none being applied
        delete newQueryParams.filters;
      }
    }

    if (action.payload.disableScroll === true) {
      newQueryParams.ds = true;
    } else {
      delete newQueryParams.ds;
    }
    return newQueryParams;
  }

  private getUpdateFilterParams(newQueryParams: { [x: string]: any }) {
    const filterParams = transformStringToParamsObject(decodeURIComponent(newQueryParams.filters || ''));

    let eraseTag = false;
    if (!filterParams) {
      eraseTag = true;
    } else {
      const tagParam = filterParams['tag'];
      if (tagParam) {
        const tagFilter = JSON.parse(tagParam);
        if (!tagFilter?.length) {
          eraseTag = true;
        }
      }
    }

    if (eraseTag) {
      delete filterParams['tag'];
      delete newQueryParams.filters;
    }

    return filterParams;
  }
}
